import * as React from 'react'
import { createPortal } from 'react-dom'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { parseChartIso } from '@/modules/imports/components/TachographChartCanvas'
import { DateField } from '@/ui/form/controls/DateField'
import { TimeField } from '@/ui/form/controls/TimeField'
import { DividerHorizontal } from '@/modules/drivers/shared/DividerHorizontal'
import { useQueryDrivers } from '@/lib/api/company/drivers'
import { useQueryVehiclesList } from '@/lib/api/company/vehicle-list'
import { getPreviousOdometerEnd } from '@/lib/api/company/chartScan'
import Checkbox from '@/modules/drivers/shared/Checkbox'
import { FieldsetSelectSmall } from '@/modules/drivers/forms/contract/inputs/FieldsetSelectSmall'
import NumberFieldInnerTable from '@/modules/drivers/forms/contract/inputs/NumberFieldInnerTable'
import { useQueryClient } from '@tanstack/react-query'
import { QUERY_KEY_CONFIG_COUNTRIES } from '@/lib/api/constants'
import AddDriverModal from '@/modules/drivers/modals/driverAdd/AddDriverModal'
import { DriverFormProvider } from '@/modules/drivers/context/DriverFormContext'
import AddVehicleModal from '@/modules/vehicles/modals/vehicleAdd/AddVehicleModal'
import { VehicleFormProvider } from '@/modules/vehicles/context/VehicleFormContext'
import { VehiclesProvider } from '@/modules/shared/context/vehicles/VehiclesContext'
import { TACHOGRAPH_STATUSES } from '@/modules/imports/container/scan-discs-popup-container/tachographStatus'
import { useChartT } from '@/modules/imports/container/scan-discs-popup-container/i18n'

const TachoDiscSummary = ({
  editor,
  companyId,
}: {
  editor: any
  companyId: string
}) => {
  const { t } = useLocalize()
  const T = useChartT()
  const statusOptions = TACHOGRAPH_STATUSES.map((s) => ({
    value: String(s.value),
    label: T(s.key, s.def),
  }))
  const queryClient = useQueryClient()
  const [isAddDriverOpen, setIsAddDriverOpen] = React.useState(false)
  const [isAddVehicleOpen, setIsAddVehicleOpen] = React.useState(false)
  const distance = Math.max(
    0,
    editor.firstVehicleOdometerEnd - editor.firstVehicleOdometerStart,
  )
  const hasDistancePoints =
    (editor.distanceData?.startPoints?.length ?? 0) + (editor.distanceData?.endPoints?.length ?? 0) > 0

  const handleDriverCreated = (driverId: number) => {
    editor.setDriverId(driverId)
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEY_CONFIG_COUNTRIES, 'drivers'],
    })
  }
  const handleVehicleCreated = (vehicleId: number) => {
    editor.setVehicleId(vehicleId)
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEY_CONFIG_COUNTRIES, 'vehicles'],
    })
  }

  return (
    <section className="tw-flex-1 tw-overflow-auto tw-px-6">
      <div className="tw-relative tw-h-full">
        <div className="tw-flex tw-flex-col tw-gap-2 tw-py-3">
          <FieldRow label="Dzień">
            <DateField
              inputSize="small"
              value={dateOnly(editor.dayStartDate)}
              setValue={(newDate) => {
                if (newDate) editor.setChartDate(newDate)
              }}
            />
          </FieldRow>

          <FieldRow label="Początek dnia">
            <TimeField
              inputSize="sm"
              value={hhmm(editor.dayStartDate)}
              setValue={(v) => v && editor.setDayStartTime(v)}
            />
          </FieldRow>

          <FieldRow label="Koniec dnia">
            <TimeField
              inputSize="sm"
              value={hhmm(editor.dayEndDate)}
              setValue={(v) => v && editor.setDayEndTime(v)}
            />
          </FieldRow>

          <FieldRow
            label="Kierowca"
            action={
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  setIsAddDriverOpen(true)
                }}
                className="tw-text-nowrap tw-text-sm tw-text-primary hover:tw-underline"
              >
                Dodaj kierowcę...
              </button>
            }
          >
            <DriverPicker companyId={companyId} editor={editor} />
          </FieldRow>

          <FieldRow
            label="Samochód"
            action={
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  setIsAddVehicleOpen(true)
                }}
                className="tw-text-nowrap tw-text-sm tw-text-primary hover:tw-underline"
              >
                Dodaj samochód...
              </button>
            }
          >
            <VehiclePicker companyId={companyId} editor={editor} />
          </FieldRow>

          <DividerHorizontal />
          <FieldRow label="Start (km)">
            <NumberFieldInnerTable
              fullWidth
              inputSize="small"
              innerLabel="km"
              allowDecimal={false}
              placeholder="0"
              value={String(editor.firstVehicleOdometerStart || '')}
              onChange={(e) =>
                editor.setOdometerStart(parseInt(e.target.value || '0', 10))
              }
            />
          </FieldRow>

          <FieldRow label="Koniec (km)">
            <NumberFieldInnerTable
              fullWidth
              inputSize="small"
              innerLabel="km"
              allowDecimal={false}
              placeholder="0"
              value={String(editor.firstVehicleOdometerEnd || '')}
              onChange={(e) =>
                editor.setOdometerEnd(parseInt(e.target.value || '0', 10))
              }
            />
          </FieldRow>

          <FieldRow label="Przebieg (km)">
            <NumberFieldInnerTable
              fullWidth
              inputSize="small"
              innerLabel="km"
              allowDecimal={false}
              readOnly
              value={String(distance)}
            />
          </FieldRow>

          <FieldRow label="Odczytany przebieg (km)">
            <NumberFieldInnerTable
              fullWidth
              inputSize="small"
              innerLabel="km"
              allowDecimal={false}
              placeholder="0"
              // Read from the distance points when the disc has them, like WPF.
              readOnly={hasDistancePoints}
              value={String(editor.distanceFromChart || (hasDistancePoints ? 0 : ''))}
              onChange={(e) =>
                editor.setDistanceFromChart(parseInt(e.target.value || '0', 10))
              }
            />
          </FieldRow>

          <FieldRow label="Status">
            <FieldsetSelectSmall
              placeholder="Wybierz..."
              value={String(editor.tachographStatus)}
              options={statusOptions}
              onChange={(e) =>
                editor.setTachographStatus(parseInt(e.target.value, 10))
              }
            />
          </FieldRow>
          <DividerHorizontal />

          <div className="tw-flex tw-gap-4 tw-pt-1">
            <label className="tw-flex tw-items-center tw-gap-2 tw-text-sm">
              <Checkbox
                checked={editor.isCrewWork}
                onChange={(checked) => editor.setIsCrewWork(checked)}
              />
              Załoga
            </label>
            <label className="tw-flex tw-items-center tw-gap-2 tw-text-sm">
              <Checkbox
                checked={editor.isClosed}
                onChange={(checked) => editor.setIsClosed(checked)}
              />
              Zamknięta
            </label>
          </div>
          {/* WPF: fill with a manual-entry rest from / to local midnight. */}
          <div className="tw-flex tw-gap-4 tw-pt-1">
            <label className="tw-flex tw-items-center tw-gap-2 tw-text-sm">
              <Checkbox
                checked={editor.addManualEntryOnDayStart}
                onChange={(checked) => editor.setAddManualEntryOnDayStart(checked)}
              />
              {T('manualEntryDayStart', 'Wpis ręczny na początku dnia')}
            </label>
            <label className="tw-flex tw-items-center tw-gap-2 tw-text-sm">
              <Checkbox
                checked={editor.addManualEntryOnDayEnd}
                onChange={(checked) => editor.setAddManualEntryOnDayEnd(checked)}
              />
              {T('manualEntryDayEnd', 'Wpis ręczny na końcu dnia')}
            </label>
          </div>
        </div>
      </div>

      {createPortal(
        <>
          {isAddDriverOpen && (
            <DriverFormProvider companyId={companyId}>
              <AddDriverModal
                companyId={companyId}
                isOpen={isAddDriverOpen}
                close={() => setIsAddDriverOpen(false)}
                onCreated={handleDriverCreated}
              />
            </DriverFormProvider>
          )}

          {isAddVehicleOpen && (
            <VehiclesProvider>
              <VehicleFormProvider companyId={companyId}>
                <AddVehicleModal
                  companyId={companyId}
                  isOpen={isAddVehicleOpen}
                  close={() => setIsAddVehicleOpen(false)}
                  onCreated={handleVehicleCreated}
                />
              </VehicleFormProvider>
            </VehiclesProvider>
          )}
        </>,
        document.body,
      )}
    </section>
  )
}

export default TachoDiscSummary

function FieldRow({
  label,
  children,
  action,
}: {
  label: string
  children: React.ReactNode
  action?: React.ReactNode
}) {
  return (
    <div className="tw-flex tw-items-center tw-gap-2">
      <label className="tw-block tw-w-[200px] tw-shrink-0 tw-text-xs tw-font-medium tw-text-slate-700">
        {label}
      </label>
      {/* Fixed 350px field column. The small contract controls (FieldsetSelectSmall,
          sm TimeField) hardcode internal max-w caps, so force their wrappers to fill
          the column and drop those caps here, keeping every row the same width. */}
      <div className="tw-w-[350px] [&>*]:!tw-w-full [&_button]:!tw-max-w-none [&_div]:!tw-max-w-none">
        {children}
      </div>
      {action}
    </div>
  )
}

function DriverPicker({
  companyId,
  editor,
}: {
  companyId: string
  editor: any
}) {
  const q = useQueryDrivers({
    companyId,
    search: '',
    pageSize: 100,
    isActive: true,
  })
  const list = (q.data as any) || []
  const drivers = Array.isArray(list) ? list : list.drivers || []
  const options = drivers.map((d: any) => ({
    value: String(d.id),
    label: d.fullName || `${d.surname} ${d.name}`,
  }))
  return (
    <FieldsetSelectSmall
      placeholder="Wybierz kierowcę..."
      value={editor.driverId != null ? String(editor.driverId) : ''}
      options={options}
      onChange={(e) =>
        editor.setDriverId(e.target.value ? Number(e.target.value) : null)
      }
    />
  )
}

function VehiclePicker({
  companyId,
  editor,
}: {
  companyId: string
  editor: any
}) {
  const q = useQueryVehiclesList({
    companyId,
    search: '',
    pageSize: 100,
    isActive: true,
  })
  const list = (q.data as any) || []
  const vehicles = Array.isArray(list) ? list : list.vehicles || []
  const options = vehicles.map((v: any) => ({
    value: String(v.id),
    label:
      v.registrationNumber || v.registrationNumberFromTachograph || `#${v.id}`,
  }))
  const editorRef = React.useRef(editor)
  editorRef.current = editor
  // WPF RefreshOdometerValues: a newly picked vehicle starts where it ended last time,
  // unless the odometer was already typed in.
  const pick = (id: number | null) => {
    editor.setVehicleId(id)
    if (id == null || editor.isOdometerModifiedByUser()) return
    getPreviousOdometerEnd(companyId, id, editor.dayStartDate)
      .then((r) => {
        const current = editorRef.current
        const previous = r.data.odometerEnd
        if (previous == null || current.vehicleId !== id || current.isOdometerModifiedByUser()) return
        current.setFirstVehicle(
          id,
          previous,
          current.firstVehicleOdometerEnd === 0 ? previous : current.firstVehicleOdometerEnd,
          false,
        )
      })
      .catch(() => undefined)
  }
  return (
    <FieldsetSelectSmall
      placeholder="Wybierz pojazd..."
      value={editor.vehicleId != null ? String(editor.vehicleId) : ''}
      options={options}
      onChange={(e) => pick(e.target.value ? Number(e.target.value) : null)}
    />
  )
}

function dateOnly(iso: string): string {
  return iso.slice(0, 10)
}
function hhmm(iso: string): string {
  const d = parseChartIso(iso)
  return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`
}

